import {LedgerReasons} from '@/types';
import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {BusinessTransactionChannel} from '@/types/AfriexBusinessTransactionChannel';
import {BusinessTransactionStatus} from '@/types/AfriexBusinessTransactionStatus';
import {BusinessTransactionType} from '@/types/AfriexBusinessTransactionType';

import {
  capitalizeWords,
  compareAndFilterFields,
  formatValues,
  generateBusinessTxAmountFields,
  getBusinessTxAmountAndAsset,
  getBusinessTxTypeCheck,
  showBusinessCurrencyRate,
} from './dashboard';
import * as formatDate from './date';
import {getProcessorOwnId} from './processorOwnId';

interface DisplayField {
  [key: string]: string | string[] | null | undefined;
}

const getTxFee = (txn: AfriexBusinessTransaction): string | null => {
  if (txn.destinationAmount?.fee) {
    return `${formatValues(Number(txn.destinationAmount.fee), 'money', 4)} ${txn.destinationAmount.currencyCode}`;
  }
  if (txn.sourceAmount?.fee) {
    return `${formatValues(Number(txn.sourceAmount.fee), 'money', 4)} ${txn.sourceAmount.currencyCode}`;
  }
  return null;
};

/**
 * The rail the money moved over. One leg of a business transaction is the
 * business wallet (the destination of a deposit, the source of a withdrawal),
 * so the channel worth showing is the other leg's.
 */
export const getBusinessTxChannel = (
  txn: AfriexBusinessTransaction,
): string | undefined => {
  const source = txn?.sourcePaymentMethod?.channel;
  const destination = txn?.destinationPaymentMethod?.channel;
  return source === BusinessTransactionChannel.WALLET && destination
    ? destination
    : source;
};

/**
 * The processor on whichever leg has one; the wallet leg never does. The
 * detail header and the detail rows both read this, so they cannot disagree.
 * Deposits saved before #7978 only have it in processorMeta.
 */
export const getBusinessTxProcessor = (
  txn: AfriexBusinessTransaction,
): string | undefined =>
  txn?.sourcePaymentMethod?.processor ||
  txn?.destinationPaymentMethod?.processor ||
  txn?.meta?.processorMeta?.processorName;

/**
 * The headline amount and currency: what reached the recipient for a payout
 * (WITHDRAW, SEND), what came in for everything else. Always a matching pair,
 * so a cross-currency transaction never shows one leg's amount in the other
 * leg's currency.
 */
export const getBusinessTxDisplayAmount = (
  txn: AfriexBusinessTransaction,
): {amount: string | number | undefined; asset: string | undefined} => {
  const {
    txType,
    initiatedAmount,
    initiatedAsset,
    fulfilledAmount,
    fulfilledAsset,
  } = getBusinessTxAmountAndAsset(txn, 'rate');
  const isPayout =
    txType === BusinessTransactionType.WITHDRAW ||
    txType === BusinessTransactionType.SEND;
  return isPayout
    ? {amount: fulfilledAmount, asset: fulfilledAsset}
    : {amount: initiatedAmount, asset: initiatedAsset};
};

/**
 * Started by the business's own staff from the business dashboard. Only that
 * builder (fromDashboardRequest) generates a `BUSINESS-` reference; a retry
 * keeps it (only a `-1`, `-2` suffix is added), and since #7048 no status
 * poll overwrites it. Such a transaction has no end customer: the user on its
 * wallet side is the staff member who started it. (PAPSS withdrawals polled
 * before #7048 lost the prefix and read as not dashboard.)
 */
export const isDashboardBusinessTx = (
  txn: AfriexBusinessTransaction,
): boolean => txn?.meta?.businessReference?.startsWith('BUSINESS-') === true;

// The business user on the wallet side (source of a payout, destination of a
// deposit).
const getWalletSideUserId = (
  txn: AfriexBusinessTransaction,
): string | undefined =>
  txn?.sourcePaymentMethod?.channel === BusinessTransactionChannel.WALLET
    ? txn.sourcePaymentMethod.userId
    : txn?.destinationPaymentMethod?.userId;

/**
 * Whose recent transactions the detail page lists: the customer's, or for a
 * dashboard transaction (no customer) the business's.
 */
export const getBusinessTxHistoryScope = (
  txn: AfriexBusinessTransaction,
): {userId: string} | {businessId: string} | undefined => {
  if (isDashboardBusinessTx(txn)) {
    return txn.businessId ? {businessId: txn.businessId} : undefined;
  }
  const userId = txn?.sourcePaymentMethod?.userId;
  return userId ? {userId} : undefined;
};

export function getBusinessTxnFieldsToDisplay(
  txn: AfriexBusinessTransaction,
): DisplayField[] {
  if (!txn) {
    return [];
  }
  const txType = getBusinessTxTypeCheck(txn);
  const txChannel = txn?.sourcePaymentMethod?.channel?.replace(/_/g, ' ');
  const txId = {'Transaction ID': txn?.id};
  const customerId = isDashboardBusinessTx(txn)
    ? {'Initiated By': getWalletSideUserId(txn)}
    : {
        'Customer ID':
          txn?.sourcePaymentMethod?.userId ||
          txn?.destinationPaymentMethod?.userId,
      };
  const businessId = {
    'Business ID': txn?.businessId,
  };
  const adminId = {
    // processorMeta.response is a raw, untyped provider payload
    // (Record<string, unknown>); adminUserId is always a string id when present.
    'Admin ID': txn?.meta?.processorMeta?.response?.adminUserId as
      string | undefined,
  };
  const ledgerTxId = {
    'Ledger Transaction IDs': txn.meta?.ledgerMeta?.ledgerTransactionIds,
  };
  const txIdFields = compareAndFilterFields(txId, ledgerTxId);
  // meta.businessReference is the reference we generate; meta.reference is
  // the merchant's, and falls back to ours when the merchant sent none, so it
  // is only shown when it differs.
  const internalReference = txn.meta?.businessReference;
  const merchantReference = txn.meta?.reference;
  const referenceFields = [
    {'Internal Reference': internalReference},
    {
      'Merchant Reference':
        merchantReference !== internalReference ? merchantReference : null,
    },
    {
      'Processor Reference': txn.meta?.processorMeta?.processorTransactionId,
    },
    {
      'Processor ID': getProcessorOwnId(
        txn.meta?.processorMeta?.processorName,
        txn.meta?.processorMeta?.response,
      ),
    },
    {
      // processorMeta.response is a raw, untyped provider payload
      // (Record<string, unknown>); sessionId is always a string when present.
      'Session ID': txn.meta?.processorMeta?.response?.sessionId as
        string | undefined,
    },
  ];
  const uetrField = {
    UETR: txn.meta?.processorMeta?.uetr,
  };
  const txAmountFields = generateBusinessTxAmountFields(txn);
  const currencyRate = showBusinessCurrencyRate(txn);

  const txFee = getTxFee(txn);

  const isPaymentReasonOthers =
    txn?.meta?.ledgerMeta?.reason === LedgerReasons.OTHERS;
  const paymentReason = [
    {
      // processorMeta.response is a raw, untyped provider payload
      // (Record<string, unknown>); note is always a string when present.
      'Payment Reason': isPaymentReasonOthers
        ? (txn?.meta?.processorMeta?.response?.note as string | undefined)
        : txn?.meta?.ledgerMeta?.reason?.replaceAll('_', ' '),
    },
  ];

  const meta =
    txn.meta?.destinationPaymentMethodMeta ?? txn.meta?.paymentMethodMeta;
  const getMetaValue = <T extends string>(key: T): string | null => {
    return meta && key in meta
      ? (meta[key as keyof typeof meta] as string)
      : null;
  };

  const paymentMethodMetaFields: Record<string, string | null | undefined>[] =
    meta
      ? [
          {
            'Bank Name':
              getMetaValue('bankName') ?? getMetaValue('institutionName'),
          },
          {
            'Bank Account #': getMetaValue('accountNumber'),
          },
          {
            'Bank Account Name':
              getMetaValue('accountName') ?? getMetaValue('recipientName'),
          },
          {
            'Swift Code':
              getMetaValue('bankSwiftCode') ?? getMetaValue('swiftCode'),
          },
          {
            'Account Address':
              getMetaValue('accountAddress') ??
              getMetaValue('recipientAddress'),
          },
          {
            'Account Email':
              getMetaValue('accountEmail') ?? getMetaValue('recipientEmail'),
          },
          {
            'Account Phone': getMetaValue('accountPhone'),
          },
          {
            Currency: getMetaValue('currency'),
          },
          {
            Country: getMetaValue('country'),
          },
          {
            Processor: getMetaValue('processor'),
          },
          {
            // The transaction's own narration, not the payment method's
            // (destinationPaymentMethodMeta can carry an unrelated stray
            // `narration` key from when that payment method was created).
            Narration: txn.meta?.narration,
          },
        ].filter((field) => {
          const value = Object.values(field)[0];
          return value !== null && value !== undefined && value !== '';
        })
      : [];

  switch (txType) {
    case BusinessTransactionType.DEPOSIT:
      return [
        ...(txChannel === 'ADMIN' ? [adminId] : [customerId]),
        businessId,
        {
          Type: capitalizeWords(txn.type?.replace(/_/g, ' ')),
        },
        ...txIdFields,
        ...referenceFields,
        {'Transaction IP Address': txn.meta?.location?.ip},
        {Status: txn.status},
        ...txAmountFields,
        ...paymentMethodMetaFields,
        {'Currency Rate': currencyRate},
        {'Source Channel': txn.sourcePaymentMethod?.channel ?? null},
        {Fee: txFee},
        {
          'Destination Business': txn.destinationPaymentMethod?.userId ?? null,
        },
        uetrField,
        ...paymentReason,
        {
          'Date Initiated': formatDate.dateTimeInTimeZone(txn.createdAt),
        },
        {
          'Date Updated': formatDate.dateTimeInTimeZone(txn.updatedAt),
        },
      ];
    case BusinessTransactionType.WITHDRAW:
      return [
        ...(txChannel === 'ADMIN' ? [adminId] : [customerId]),
        businessId,
        {
          Type: capitalizeWords(txn.type?.replace(/_/g, ' ')),
        },
        ...txIdFields,
        ...referenceFields,
        {'Transaction IP Address': txn.meta?.location?.ip},
        {
          'Date Initiated': formatDate.dateTimeInTimeZone(txn.createdAt),
        },
        {
          'Date Updated': formatDate.dateTimeInTimeZone(txn.updatedAt),
        },
        ...paymentMethodMetaFields,
        uetrField,
        ...paymentReason,
        {Processor: getBusinessTxProcessor(txn)},
        ...(txn.status === BusinessTransactionStatus.FAILED
          ? [
              {
                // processorMeta.response is a raw, untyped provider payload
                // (Record<string, unknown>); both fields are strings when present.
                'Failed Reason':
                  (txn.meta?.processorMeta?.response?.statusMessage as
                    string | undefined) ||
                  (txn.meta?.processorMeta?.response?.statusDescription as
                    string | undefined) ||
                  'No reason provided',
              },
            ]
          : []),
        ...txAmountFields,
        {'Currency Rate': currencyRate},
        {Channel: getBusinessTxChannel(txn) ?? null},
        {Fee: txFee},
        {Status: txn.status},
      ];
    default:
      return [
        ...(txChannel === 'ADMIN' ? [adminId] : [customerId]),
        businessId,
        {
          Type: capitalizeWords(txn.type?.replace(/_/g, ' ')),
        },
        ...txIdFields,
        ...referenceFields,
        {'Transaction IP Address': txn.meta?.location?.ip},
        {
          'Date Initiated': formatDate.dateTimeInTimeZone(txn.createdAt),
        },
        {
          'Date Updated': formatDate.dateTimeInTimeZone(txn.updatedAt),
        },
        uetrField,
        ...paymentReason,
        {Processor: getBusinessTxProcessor(txn)},
        ...txAmountFields,
        {'Currency Rate': currencyRate},
        {Channel: getBusinessTxChannel(txn) ?? null},
        {Fee: txFee},
        {Status: txn.status},
      ];
  }
}
